import unescapedText from 'I18n/utils/unescapedText';
import { ComponentType, useMemo } from 'react';
import { useLocation } from 'quartz-routing/react-router';
import { Paths } from '../../constants/paths';
import { usePartnerTierIconMap } from '../../utils/getPartnerTierContent';
import { getFlattenedOptions } from '../filterUtils';
import { useAppliedFilters } from './useAppliedFilters';
import { useAsyncFilterOptions } from './useAsyncFilterOptions';

export interface FilterTag {
  field: string;
  icon?: ComponentType;
  text: string;
  tooltip: string;
  value: string;
}

export function useFilterTags() {
  const appliedFilters = useAppliedFilters();
  const location = useLocation();
  const partnerTierIconMap = usePartnerTierIconMap();
  const asyncFilterOptionsResult = useAsyncFilterOptions();

  const filterTags = useMemo(() => {
    const tags: FilterTag[] = [];

    for (const appliedFilter of appliedFilters) {
      if (
        appliedFilter.field === 'PRODUCT_TYPE' &&
        (location.pathname === Paths.SEARCH_RESULTS ||
          location.pathname === Paths.SEARCH_RESULTS_PRODUCTS)
      ) {
        continue;
      }

      // Special handling for language filters
      if (
        appliedFilter.field === 'APP_LANGUAGES' ||
        appliedFilter.field === 'PROFILE_LANGUAGES'
      ) {
        tags.push(
          ...appliedFilter.values.map((value) => ({
            field: appliedFilter.field,
            text: unescapedText('marketplaceStorefront.filters.filterChip', {
              label: appliedFilter.label ?? appliedFilter.placeholder ?? '',
              value: unescapedText(`SharedI18nStrings.languageNames.${value}`),
            }),
            tooltip: appliedFilter.label ?? appliedFilter.placeholder ?? '',
            value,
          }))
        );
        continue;
      }

      const asyncOptions =
        asyncFilterOptionsResult.data?.result.filterOptions[
          appliedFilter.field
        ] ?? [];

      // Flatten the options if necessary
      const options = appliedFilter.hasMoreOptions
        ? asyncOptions
        : getFlattenedOptions(appliedFilter);

      // Async filter still loading? Just hide
      if (appliedFilter.hasMoreOptions && options.length === 0) continue;

      // Toggle filters, such as Installable, Breeze, and Certified, have no options
      if (options.length === 0) {
        tags.push({
          field: appliedFilter.field,
          text: appliedFilter.label ?? appliedFilter.placeholder ?? '',
          value: appliedFilter.values[0],
          // Don't show a tooltip for toggle filters, as they are self-explanatory
          tooltip: '',
        });
        continue;
      }

      for (const option of options) {
        if (appliedFilter.values.includes(option.value)) {
          const isToggle = appliedFilter.dataType === 'BOOLEAN';
          tags.push({
            field: appliedFilter.field,
            icon:
              appliedFilter.field === 'PROFILE_SOLUTIONS_PARTNER_TIER'
                ? partnerTierIconMap?.[option.value]
                : undefined,
            text: isToggle
              ? option.text
              : unescapedText('marketplaceStorefront.filters.filterChip', {
                  label: appliedFilter.label ?? appliedFilter.placeholder ?? '',
                  value: option.text,
                }),
            tooltip: appliedFilter.label ?? appliedFilter.placeholder ?? '',
            value: option.value,
          });
        }
      }
    }

    return tags;
  }, [
    appliedFilters,
    asyncFilterOptionsResult.data,
    location.pathname,
    partnerTierIconMap,
  ]);

  return filterTags;
}
